import { supabase, TABLES } from './db';

export type PromptTagType = 'link' | 'keyword' | 'env_var' | 'status' | 'tool';

export const STATUS_OPTIONS = ['blocked', 'done', 'review-needed', 'question'] as const;
export type StatusOption = typeof STATUS_OPTIONS[number];

export interface PromptTag {
  type: PromptTagType;
  value: string;
  label?: string;
}

export interface PromptPost {
  id: string;
  taskId: string;
  authorId: string;
  authorName: string;
  content: string;
  tags: PromptTag[];
  createdAt: string;
  reactions?: Record<string, string[]>;
  replyTo?: string;
}

export async function createPromptPost(
  post: Omit<PromptPost, 'id' | 'createdAt'>
): Promise<string> {
  const now = new Date().toISOString();
  const { data, error } = await supabase
    .from(TABLES.PROMPT_POSTS)
    .insert({
      task_id: post.taskId,
      author_id: post.authorId,
      content: post.content,
      reactions: (post.reactions as object) ?? {},
      created_at: now,
    })
    .select('id')
    .single();
  if (error) throw error;
  return (data as { id: string }).id;
}

export function subscribePromptPosts(
  taskId: string,
  callback: (posts: PromptPost[]) => void
): () => void {
  const load = async () => {
    const { data, error } = await supabase
      .from(TABLES.PROMPT_POSTS)
      .select('*')
      .eq('task_id', taskId)
      .order('created_at', { ascending: false })
      .limit(50);
    if (!error) callback(mapPosts(data ?? []));
  };

  void load();

  const channel = supabase
    .channel(`prompt-posts-${taskId}`)
    .on('postgres_changes', { event: '*', schema: 'public', table: TABLES.PROMPT_POSTS, filter: `task_id=eq.${taskId}` }, () => void load())
    .subscribe();

  return () => { void supabase.removeChannel(channel); };
}

export async function addReaction(
  postId: string,
  emoji: string,
  userId: string,
  hasReacted: boolean
): Promise<void> {
  const { data } = await supabase.from(TABLES.PROMPT_POSTS).select('reactions').eq('id', postId).maybeSingle();
  const reactions = (data?.reactions as Record<string, string[]>) ?? {};
  const users: string[] = reactions[emoji] ?? [];
  reactions[emoji] = hasReacted ? users.filter((u) => u !== userId) : [...users, userId];
  const { error } = await supabase.from(TABLES.PROMPT_POSTS).update({ reactions }).eq('id', postId);
  if (error) throw error;
}

function mapPosts(rows: Record<string, unknown>[]): PromptPost[] {
  return rows.map((r) => ({
    id: r['id'] as string,
    taskId: (r['task_id'] as string) ?? '',
    authorId: (r['author_id'] as string) ?? '',
    authorName: (r['author_name'] as string) ?? '',
    content: (r['content'] as string) ?? '',
    tags: (r['tags'] as PromptTag[]) ?? [],
    createdAt: (r['created_at'] as string) ?? new Date().toISOString(),
    reactions: (r['reactions'] as Record<string, string[]>) ?? {},
    replyTo: r['reply_to'] as string | undefined,
  }));
}
